/**
 * Årshjul — the mosque's internal annual planner.  MKK adoption Batch 1 (MKK_ADOPTION_BATCH_1.md §7); hardened 2026-10-02.
 *
 * Behind the `planner` capability and planner.read (planner.write to change it). Two views of the same occurrences:
 * «Agenda» (the default: the next eight weeks, grouped by week, the Islamic date beside each day) and «Måned» (a
 * compact month of day buttons; a day opens its list). Recurrence is one rule computed by the server; a single date can
 * be cancelled and restored without touching the series, and a cancelled date is SHOWN as cancelled, never hidden.
 *
 * The Islamic date follows the mosque's own moon-sighting adjustment (the server sends the adjustments with the window;
 * hijri.activeOffset picks the one in force on each day), so it matches the prayer timetable and the app.
 *
 * The month is a labelled group of buttons, each naming its day and how much is on it — not an ARIA grid, which promises
 * arrow-key movement it did not have. Tab moves through the days; the weekday header is decoration for sighted readers.
 *
 * Internal: nothing here is public, and nothing is synced to Google or Microsoft (not in Batch 1). A public event is
 * still made under Program; a planner item can point at one.
 *
 * Work hub (2026-10-31): a yearly item may follow a Hijri day («Følg hijri-kalenderen»: Eid, Ramadan, Ashura, Mawlid —
 * planned once, right every year, with the mosque's moon-sighting adjustment); «Legg til faste poster» offers the posts
 * most years have (Årsmøte, Ramadan-forberedelser, both Eids, Nikah-kveld, Styremøte, Dugnad). Nothing is added by itself.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { can, post, patch, send, errText, type Body } from '../lib/api';
import { useLoad, useMsg } from '../lib/hooks';
import { useDirty } from '../lib/nav';
import { Btn, Card, Field, Loading, LoadError, Msg, Tag, TwoStep } from '../components/ui';
import { Day, Hijri, Planner } from '../core';
import { FormDialog } from '../components/dialog';
import { BackButton, Conflict, EventSelect, PersonSelect, eventText, useAssignable, useEscapeToClose, useEvents } from './work-ui';

const WD = ['søn', 'man', 'tir', 'ons', 'tor', 'fre', 'lør'];
const MONTH = ['januar', 'februar', 'mars', 'april', 'mai', 'juni', 'juli', 'august', 'september', 'oktober', 'november', 'desember'];
const dayLabel = (s: string) => { const p = Day.parse(s)!; return `${WD[Day.weekday(s)]} ${p.d}. ${MONTH[p.m - 1].slice(0, 3)}`; };
type Adjustment = { effective_from: string; offset_days: number; created_at?: string };
/** The Islamic date of a day, with the mosque's moon-sighting adjustment in force on that day. */
const hijriOf = (s: string, adj: Adjustment[]) => {
  const p = Day.parse(s)!;
  return Hijri.format(Hijri.toHijri(new Date(p.y, p.m - 1, p.d), Hijri.activeOffset(adj, s)));
};
/** Monday of the week a day is in. */
const monday = (s: string) => Day.add(s, -((Day.weekday(s) + 6) % 7));

/** One occurrence in a list. A component of its own (it was defined inside the view, so every render made a new type). */
function OccRow({ o, it, onOpen }: { o: Body; it: Body; onOpen: (o: Body) => void }) {
  return (
    <li>
      <button type="button" id={`occ-${o.itemId}-${o.date}`} className={'work-row' + (o.cancelled ? ' cancelled' : '')} onClick={() => onOpen(o)}>
        <span className="work-row-title">{it.startTime ? <span className="work-time">{it.startTime}{it.endTime ? '–' + it.endTime : ''}</span> : <span className="work-time">Hele dagen</span>} {it.title}</span>
        <span className="work-row-meta">
          <Tag tone="grey">{Planner.CATEGORY_NO[it.category] || it.category}</Tag>
          {o.cancelled && <Tag tone="red">Avlyst denne dagen</Tag>}
          {it.recurrenceText && <span>{it.recurrenceText}</span>}
          {it.location && <span>· {it.location}</span>}
        </span>
      </button>
    </li>
  );
}

export function Arshjul() {
  const write = can('planner.write');
  const summary = useLoad<Body>('/work/summary');
  const today: string | null = summary.data ? summary.data.today : null;
  const [mode, setMode] = useState<'agenda' | 'month'>('agenda');
  const [month, setMonth] = useState<{ y: number; m: number } | null>(null);
  const [category, setCategory] = useState('');
  const [open, setOpen] = useState<{ id: string; date: string } | 'new' | null>(null);
  const [day, setDay] = useState<string | null>(null);
  // Owner 2026-10-23: «+ Nytt punkt» opens the form in a popup; an existing item keeps the detail beside the list.
  const [newDirty, setNewDirty] = useState(false);
  const [seeding, setSeeding] = useState(false);
  useEffect(() => { if (today && !month) { const p = Day.parse(today)!; setMonth({ y: p.y, m: p.m }); } }, [today, month]);
  const range = !today || !month ? null : mode === 'agenda' ? { from: today, to: Day.add(today, 55) }
    : { from: Day.fmt(month.y, month.m, 1), to: Day.fmt(month.y, month.m, Day.daysInMonth(month.y, month.m)) };
  const { data, error, reload } = useLoad<Body>(range ? `/work/planner?from=${range.from}&to=${range.to}` : null);
  const people = useAssignable(write);
  const adj: Adjustment[] = (data && data.hijriAdjustments) || [];
  const byId = useMemo(() => new Map<string, Body>(((data && data.items) || []).map((i: Body) => [i.id, i] as [string, Body])), [data]);
  const occ: Body[] = ((data && data.occurrences) || []).filter((o: Body) => !category || (byId.get(o.itemId) || ({} as Body)).category === category)
    .sort((a: Body, b: Body) => a.date.localeCompare(b.date) || String((byId.get(a.itemId) || ({} as Body)).startTime || '').localeCompare(String((byId.get(b.itemId) || ({} as Body)).startTime || '')));
  const openOcc = useCallback((o: Body) => setOpen({ id: o.itemId, date: o.date }), []);
  // Back to the row the detail came from (or the heading): read from a ref, so closing is one plain state change.
  const openRef = useRef(open);
  openRef.current = open;
  const closeDetail = useCallback(() => {
    const cur = openRef.current;
    const back = cur && cur !== 'new' ? `occ-${cur.id}-${cur.date}` : 'planner-head';
    setOpen(null);
    setTimeout(() => (document.getElementById(back) || document.getElementById('planner-head'))?.focus(), 0);
  }, []);

  if (summary.error) return <LoadError error={summary.error} retry={summary.reload} />;
  const weeks: [string, Body[]][] = [];
  for (const o of occ) { const w = monday(o.date); const last = weeks[weeks.length - 1]; if (last && last[0] === w) last[1].push(o); else weeks.push([w, [o]]); }
  const row = (o: Body) => <OccRow key={o.itemId + o.date} o={o} it={byId.get(o.itemId) || {}} onOpen={openOcc} />;

  return (
    <div className={'work-split' + (open && open !== 'new' ? ' has-detail' : '')}>
      <Card className="work-list" title={<span id="planner-head" tabIndex={-1}>Årshjul</span>}
            hint="Moskeens egen plan for året: møter, forberedelser, frister og arrangementer. Bare ansatte ser årshjulet; offentlige arrangementer lages under Arrangementer.">
        <div className="work-bar">
          <div className="bnr-seg work-seg" role="group" aria-label="Visning">
            <button type="button" aria-pressed={mode === 'agenda'} onClick={() => setMode('agenda')}>Agenda</button>
            <button type="button" aria-pressed={mode === 'month'} onClick={() => setMode('month')}>Måned</button>
          </div>
          <span className="sp" />
          {write && <Btn ghost onClick={() => setSeeding(true)}>Legg til faste poster</Btn>}
          {write && <Btn onClick={() => setOpen('new')}>+ Nytt punkt</Btn>}
        </div>
        <div className="work-bar">
          <Field label="Kategori" className="field work-search">{(id) => (
            <select id={id} value={category} onChange={(e) => setCategory(e.target.value)}>
              <option value="">Alle</option>{Planner.CATEGORIES.map((c) => <option key={c} value={c}>{Planner.CATEGORY_NO[c]}</option>)}
            </select>
          )}</Field>
          {mode === 'month' && month && (
            <div className="row work-month-nav" role="group" aria-label="Velg måned">
              <Btn ghost aria-label="Forrige måned" onClick={() => { setMonth(Day.addMonths(month.y, month.m, -1)); setDay(null); }}>‹</Btn>
              <b aria-live="polite">{MONTH[month.m - 1]} {month.y}</b>
              <Btn ghost aria-label="Neste måned" onClick={() => { setMonth(Day.addMonths(month.y, month.m, 1)); setDay(null); }}>›</Btn>
            </div>
          )}
        </div>
        {error ? <LoadError error={error} retry={reload} /> : !data || !today ? <Loading /> : mode === 'agenda' ? (
          <>
            {occ.length === 0 && <p className="card-body work-empty">Ingenting i årshjulet de neste åtte ukene.</p>}
            {weeks.map(([w, list]) => (
              <section key={w} className="work-group" aria-label={`Uken fra ${dayLabel(w)}`}>
                <h4>Uken fra {dayLabel(w)}</h4>
                {[...new Set(list.map((o) => o.date))].map((d) => (
                  <div key={d} className="work-day">
                    <p className="work-day-head"><time dateTime={d}>{dayLabel(d)}</time>{d === today && <> <Tag tone="green">I dag</Tag></>} <span className="note">{hijriOf(d, adj)}</span></p>
                    <ul className="work-rows">{list.filter((o) => o.date === d).map(row)}</ul>
                  </div>
                ))}
              </section>
            ))}
          </>
        ) : month && (
          <MonthGrid month={month} today={today} occ={occ} day={day} setDay={setDay} row={row} adj={adj} />
        )}
        <p className="note card-body">{adj.length
          ? 'Islamske datoer følger moskeens justering for månesyn (Bønnetider → Hijri-justering).'
          : 'Islamske datoer er beregnet. Moskeen har ingen justering for månesyn.'}</p>
      </Card>
      <FormDialog open={seeding} title="Legg til faste poster" onClose={() => setSeeding(false)}
        hint="Velg postene moskeen vil ha i årshjulet. Islamske dager følger hijri-kalenderen og flytter seg av seg selv hvert år.">
        {seeding && <Seeds onDone={() => { setSeeding(false); void reload(); }} />}
      </FormDialog>
      <FormDialog open={open === 'new'} title="Nytt punkt i årshjulet" dirty={newDirty} onClose={() => { setOpen(null); setNewDirty(false); }}>
        {open === 'new' && <ItemForm people={people} today={today || ''} onDirty={setNewDirty} onCancel={() => { setOpen(null); setNewDirty(false); }}
          onSaved={(id, date) => { void reload(); setNewDirty(false); setOpen({ id, date }); }} />}
      </FormDialog>
      {open && open !== 'new' && (
        <ItemDetail key={open.id + open.date} id={open.id} date={open.date} write={write} people={people} summary={summary.data} adj={adj}
            onClose={closeDetail} onChanged={() => void reload()} />
      )}
    </div>
  );
}

/** «Legg til faste poster»: the seed catalogue with each post's first day; the chosen ones are added in one go. */
function Seeds({ onDone }: { onDone: () => void }) {
  const { data, error, reload } = useLoad<Body>('/work/planner/seeds');
  const [picked, setPicked] = useState<string[]>([]);
  const [msg, say] = useMsg();
  if (error) return <LoadError error={error} retry={reload} />;
  if (!data) return <Loading />;
  const add = async () => {
    const r = await post('/work/planner/seeds', { keys: picked });
    if (!r.ok) return say(errText(r.body), 'err');
    const n = r.body.created.length, skip = r.body.skipped.length;
    say(`${n} ${n === 1 ? 'post er lagt til' : 'poster er lagt til'} i årshjulet.${skip ? ` ${skip} fantes fra før og ble ikke lagt til igjen.` : ''}`, 'ok');
    onDone();
  };
  return (
    <>
      <fieldset className="field">
        <legend>Faste poster</legend>
        {(data.items as Body[]).map((x) => (
          <label key={x.key} style={{ display: 'block', margin: '6px 0' }}>
            <input type="checkbox" disabled={x.exists} checked={picked.includes(x.key)}
              onChange={(e) => setPicked(e.target.checked ? [...picked, x.key] : picked.filter((k) => k !== x.key))} />{' '}
            <b>{x.title}</b> <span className="note">{x.exists ? 'finnes allerede' : `første gang ${x.startsOn ? dayLabel(x.startsOn) : '—'}${x.recurrenceText ? ` · ${x.recurrenceText}` : ''}`}</span>
          </label>
        ))}
      </fieldset>
      <div className="row"><Btn small={false} disabled={!picked.length} onClick={add}>Legg til {picked.length || ''}</Btn></div>
      <Msg text={msg.text} tone={msg.tone} />
    </>
  );
}

function MonthGrid({ month, today, occ, day, setDay, row, adj }: {
  month: { y: number; m: number }; today: string; occ: Body[]; day: string | null; setDay: (d: string | null) => void;
  row: (o: Body) => React.ReactElement; adj: Adjustment[];
}) {
  const first = Day.fmt(month.y, month.m, 1);
  const lead = (Day.weekday(first) + 6) % 7;
  const n = Day.daysInMonth(month.y, month.m);
  const cells: (string | null)[] = [...Array(lead).fill(null), ...Array.from({ length: n }, (_, i) => Day.fmt(month.y, month.m, i + 1))];
  const count = (d: string) => occ.filter((o) => o.date === d && !o.cancelled).length;
  return (
    <div className="card-body">
      <div className="work-month" role="group" aria-label={`${MONTH[month.m - 1]} ${month.y}: velg en dag`}>
        <div className="work-month-row" aria-hidden="true">{['man', 'tir', 'ons', 'tor', 'fre', 'lør', 'søn'].map((w) => <span key={w} className="work-month-wd">{w}</span>)}</div>
        {Array.from({ length: Math.ceil(cells.length / 7) }, (_, r) => (
          <div className="work-month-row" key={r}>{cells.slice(r * 7, r * 7 + 7).map((d, i) => d ? (
            <span key={d}>
              <button type="button" className={'work-month-day' + (d === today ? ' today' : '') + (d === day ? ' sel' : '')} aria-pressed={d === day}
                      aria-label={`${dayLabel(d)}${d === today ? ', i dag' : ''}: ${count(d) === 0 ? 'ingenting' : count(d) === 1 ? '1 punkt' : count(d) + ' punkter'}`} onClick={() => setDay(d === day ? null : d)}>
                {Day.parse(d)!.d}{count(d) > 0 && <span className="work-dot" aria-hidden="true">{count(d) > 1 ? count(d) : '•'}</span>}
              </button>
            </span>
          ) : <span key={'e' + i} aria-hidden="true" />)}</div>
        ))}
      </div>
      {day && (
        <section aria-label={dayLabel(day)}>
          <h4><time dateTime={day}>{dayLabel(day)}</time> <span className="note">{hijriOf(day, adj)}</span></h4>
          {occ.filter((o) => o.date === day).length === 0 ? <p className="note">Ingenting denne dagen.</p>
            : <ul className="work-rows">{occ.filter((o) => o.date === day).map(row)}</ul>}
        </section>
      )}
    </div>
  );
}

function ItemDetail({ id, date, write, people, summary, adj, onClose, onChanged }: {
  id: string; date: string; write: boolean; people: { id: string; name: string }[]; summary: Body | null; adj: Adjustment[]; onClose: () => void; onChanged: () => void;
}) {
  const { data, error, reload, setData } = useLoad<Body>('/work/planner/' + id);
  const [msg, say] = useMsg();
  const [editing, setEditing] = useState(false);
  const [conflict, setConflict] = useState(false);
  const [busy, setBusy] = useState(false);
  const [formDirty, setFormDirty] = useState(false);
  const head = useRef<HTMLSpanElement>(null);
  useEffect(() => { if (data) head.current?.focus(); }, [Boolean(data)]); // eslint-disable-line react-hooks/exhaustive-deps
  useEscapeToClose(head, onClose, (editing && formDirty) || busy);
  if (error) return <Card className="work-detail"><div className="card-body"><BackButton onClick={onClose} what="punktet" /></div><LoadError error={error} retry={reload} /></Card>;
  if (!data) return <Card className="work-detail"><Loading /></Card>;
  const it: Body = data.item;
  const cancelled = (data.cancelledDates || []).includes(date);
  const caps = (summary && summary.capabilities) || {};
  const occurrence = async (c: boolean) => {
    setBusy(true); say('Lagrer …');
    const r = await send(`/work/planner/${id}/occurrences/${date}`, { method: 'PUT', body: JSON.stringify({ cancelled: c }) });
    setBusy(false);
    if (!r.ok) return say(errText(r.body), 'err');
    setData(r.body); onChanged(); say(c ? 'Denne dagen er avlyst. Resten av serien står.' : 'Denne dagen er gjenopprettet.', 'ok');
  };
  const series = async (body: Body, done: string, fromForm = false) => {
    setBusy(true); say('Lagrer …');
    const r = await patch('/work/planner/' + id, { ...body, version: it.version });
    setBusy(false);
    if (r.status === 409 && r.body.item) {
      setData(r.body);
      // An edit in the form keeps what was typed (the form stays open) and asks; a one-click change just says so.
      if (fromForm) { setConflict(true); return say('', ''); }
      return say(r.body.message + ' Endringen din ble ikke lagret.', 'warn');
    }
    if (!r.ok) return say(errText(r.body, { title: 'Tittel', startsOn: 'Dato', startTime: 'Starttid', endTime: 'Sluttid', recurrence: 'Gjentakelse' }), 'err');
    setData(r.body); onChanged(); setEditing(false); setConflict(false);
    say(done + (r.body.cancellationsDropped ? ` ${r.body.cancellationsDropped} avlyste dager falt bort fordi de ikke lenger er i serien.` : ''), 'ok');
  };
  const makeTask = async () => {
    setBusy(true);
    const r = await post('/work/tasks', { title: it.title, dueOn: date, plannerItemId: id, ...(it.eventId ? { eventId: it.eventId } : {}) });
    setBusy(false);
    if (!r.ok) return say(errText(r.body), 'err');
    say('Oppgaven er laget. Du finner den under Oppgaver.', 'ok');
  };
  // «Lag utkast til arrangement» (Arrangementer pro, 2026-10-31): a draft event from this day of the item, linked back.
  const makeDraft = async () => {
    setBusy(true);
    const r = await post(`/work/planner/${id}/draft-event`, { date });
    setBusy(false);
    if (!r.ok) return say(errText(r.body), 'err');
    setData(r.body); onChanged(); say(r.body.message_no || 'Utkast laget under Arrangementer.', 'ok');
  };
  return (
    <Card className="work-detail" title={<span ref={head} tabIndex={-1}>{it.title}</span>}>
      <div className="card-body">
        <BackButton onClick={onClose} what="punktet" />
        <p className="work-row-meta"><Tag tone="grey">{Planner.CATEGORY_NO[it.category]}</Tag>{cancelled && <Tag tone="red">Avlyst denne dagen</Tag>}{it.status === 'cancelled' && <Tag tone="red">Hele serien er avlyst</Tag>}</p>
        <dl className="kv">
          <dt>Dag</dt><dd><time dateTime={date}>{dayLabel(date)}</time> <span className="note">{hijriOf(date, adj)}</span></dd>
          <dt>Tid</dt><dd>{it.startTime ? `${it.startTime}${it.endTime ? '–' + it.endTime : ''}` : 'Hele dagen'}</dd>
          {it.recurrenceText && <><dt>Gjentakelse</dt><dd>{it.recurrenceText}</dd></>}
          {it.location && <><dt>Sted</dt><dd>{it.location}</dd></>}
          {it.owner && <><dt>Ansvarlig</dt><dd>{it.owner.name}</dd></>}
          {it.event && <><dt>Arrangement</dt><dd>{eventText(it.event)}</dd></>}
        </dl>
        {it.description && <p className="work-desc">{it.description}</p>}
        {write && it.status === 'active' && (
          <div className="row" role="group" aria-label="Handlinger">
            {it.recurrence && (cancelled ? <Btn disabled={busy} onClick={() => occurrence(false)}>Gjenopprett denne dagen</Btn>
              : <TwoStep label="Avlys bare denne dagen" confirmLabel="Ja, avlys denne dagen" disabled={busy} onConfirm={() => occurrence(true)} />)}
            <Btn ghost onClick={() => setEditing(!editing)} aria-expanded={editing}>{it.recurrence ? 'Endre serien' : 'Endre'}</Btn>
            {caps.tasks && can('tasks.manage') && <Btn ghost disabled={busy} onClick={makeTask}>Lag oppgave for denne dagen</Btn>}
            {!it.eventId && can('events.write') && <Btn ghost disabled={busy} onClick={makeDraft}>Lag utkast til arrangement</Btn>}
            {caps.checklists && can('checklists.manage') && (
              <Btn ghost onClick={() => { location.hash = `/sjekklister?new=1&date=${date}&planner=${id}`; }}>Lag sjekkliste for denne dagen</Btn>
            )}
            <TwoStep label={it.recurrence ? 'Avlys hele serien' : 'Avlys'} confirmLabel="Ja, avlys" disabled={busy} onConfirm={() => series({ status: 'cancelled' }, 'Punktet er avlyst.')} />
          </div>
        )}
        {write && it.status === 'cancelled' && <Btn disabled={busy} onClick={() => series({ status: 'active' }, 'Punktet er aktivt igjen.')}>Gjør aktivt igjen</Btn>}
        <Msg text={msg.text} tone={msg.tone} />
        {editing && <ItemForm item={it} people={people} today={date} conflict={conflict} busy={busy} onDirty={setFormDirty}
          onCancel={() => { setEditing(false); setConflict(false); }} onSubmit={(b) => series(b, 'Endringen er lagret.', true)}
          onDiscard={() => { setConflict(false); setEditing(false); say('Endringene dine er forkastet. Dette er den nyeste versjonen.', 'ok'); }} />}
      </div>
    </Card>
  );
}

/** A new item (POST), or the series' fields for an edit (onSubmit with only what the form holds). */
function ItemForm({ item, people, today, onCancel, onSaved, onSubmit, conflict = false, busy = false, onDiscard, onDirty }: {
  item?: Body; people: { id: string; name: string }[]; today: string; onCancel: () => void; onSaved?: (id: string, date: string) => void; onSubmit?: (b: Body) => void;
  /** An edit that met a newer version: the form keeps what was typed and asks (Conflict). */
  conflict?: boolean; busy?: boolean; onDiscard?: () => void; onDirty?: (dirty: boolean) => void;
}) {
  const ev = useEvents();
  const r0 = (item && item.recurrence) || null;
  const init = { title: item ? item.title : '', description: (item && item.description) || '', category: item ? item.category : 'meeting',
    startsOn: item ? item.startsOn : today, allDay: item ? !item.startTime : false, startTime: (item && item.startTime) || '18:00', endTime: (item && item.endTime) || '',
    location: (item && item.location) || '', ownerId: (item && item.ownerId) || '', eventId: (item && item.eventId) || '',
    freq: r0 ? r0.freq : '', interval: r0 ? String(r0.interval) : '1', by: (r0 && r0.by) || 'day', until: r0 ? r0.until : '',
    hijri: Boolean(item && item.hijriMonth), hijriMonth: item && item.hijriMonth ? String(item.hijriMonth) : '9', hijriDay: item && item.hijriDay ? String(item.hijriDay) : '1' };
  // What "unsaved" is measured against: the item as it was when the form opened — not a newer version that arrived since.
  const [base] = useState(init);
  const [f, setF] = useState(init);
  const [msg, say] = useMsg();
  const [saving, setSaving] = useState(false);
  const head = useRef<HTMLHeadingElement>(null);
  useEffect(() => { head.current?.focus(); }, []);
  const dirty = JSON.stringify(f) !== JSON.stringify(base);
  useDirty('planner-item', dirty);
  useEffect(() => { if (onDirty) onDirty(dirty); }, [dirty, onDirty]);
  useEscapeToClose(head, onCancel, Boolean(item) || dirty || saving);
  const set = (k: keyof typeof f) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => setF({ ...f, [k]: e.target.value });
  const defaultUntil = () => { const p = Day.parse(f.startsOn); return p ? Day.fmt(p.y + 1, p.m, Math.min(p.d, Day.daysInMonth(p.y + 1, p.m))) : ''; };
  const body = (): Body => ({ title: f.title, description: f.description || null, category: f.category, startsOn: f.startsOn,
    startTime: f.allDay ? null : f.startTime || null, endTime: f.allDay ? null : f.endTime || null, location: f.location || null,
    ownerId: f.ownerId || null, ...(ev.allowed ? { eventId: f.eventId || null } : {}),
    recurrence: f.freq ? { freq: f.freq, interval: Number(f.interval) || 1, until: f.until || defaultUntil(), ...(f.freq === 'monthly' ? { by: f.by } : {}) } : null,
    ...(f.freq === 'yearly' && f.hijri ? { hijriMonth: Number(f.hijriMonth), hijriDay: Number(f.hijriDay) } : { hijriMonth: null, hijriDay: null }) });
  const save = async () => {
    if (onSubmit) return onSubmit(body());
    setSaving(true); say('Lagrer …');
    const r = await post('/work/planner', body());
    setSaving(false);
    if (!r.ok) return say(errText(r.body, { title: 'Tittel', startsOn: 'Dato', startTime: 'Starttid', endTime: 'Sluttid', recurrence: 'Gjentakelse', category: 'Kategori' }), 'err');
    onSaved && onSaved(r.body.item.id, r.body.item.startsOn);
  };
  const form = (
    <div className={item ? 'work-edit' : undefined}>
      {item && <h4 ref={head} tabIndex={-1}>{item.recurrence ? 'Endre serien' : 'Endre punktet'}</h4>}
      {conflict && onDiscard && <Conflict what="punktet" busy={busy} onKeepMine={() => void save()} onTakeTheirs={onDiscard} />}
      <Field label="Tittel" required>{(id) => <input id={id} value={f.title} onChange={set('title')} maxLength={160} />}</Field>
      <div className="row">
        <Field label="Kategori">{(id) => (
          <select id={id} value={f.category} onChange={set('category')}>{Planner.CATEGORIES.map((c) => <option key={c} value={c}>{Planner.CATEGORY_NO[c]}</option>)}</select>
        )}</Field>
        <Field label={f.freq ? 'Første dag' : 'Dag'} required>{(id) => <input id={id} type="date" value={f.startsOn} onChange={set('startsOn')} />}</Field>
      </div>
      <label className="work-check"><input type="checkbox" checked={f.allDay} onChange={(e) => setF({ ...f, allDay: e.target.checked })} /> Hele dagen</label>
      {!f.allDay && (
        <div className="row">
          <Field label="Fra">{(id) => <input id={id} type="time" value={f.startTime} onChange={set('startTime')} />}</Field>
          <Field label="Til" note="Valgfritt">{(id, nid) => <input id={id} type="time" value={f.endTime} onChange={set('endTime')} aria-describedby={nid} />}</Field>
        </div>
      )}
      <fieldset className="work-recur">
        <legend>Gjentakelse</legend>
        <div className="row">
          <Field label="Gjentas">{(id) => (
            <select id={id} value={f.freq} onChange={set('freq')}><option value="">Ikke</option><option value="weekly">Ukentlig</option><option value="monthly">Månedlig</option><option value="yearly">Årlig</option></select>
          )}</Field>
          {f.freq && <Field label={f.freq === 'weekly' ? 'Hver … uke' : f.freq === 'monthly' ? 'Hver … måned' : 'Hvert … år'}>{(id) => (
            <input id={id} type="number" min={1} max={f.freq === 'yearly' ? 5 : 12} value={f.interval} onChange={set('interval')} />
          )}</Field>}
        </div>
        {f.freq === 'monthly' && (
          <Field label="Hvilken dag i måneden">{(id) => (
            <select id={id} value={f.by} onChange={set('by')}>
              <option value="day">Samme dato (en kortere måned får siste dag)</option>
              <option value="weekday">Samme ukedag, for eksempel 2. lørdag</option>
              <option value="last_weekday">Siste samme ukedag i måneden</option>
            </select>
          )}</Field>
        )}
        {f.freq === 'yearly' && (
          <>
            <label className="work-check"><input type="checkbox" checked={f.hijri} onChange={(e) => setF({ ...f, hijri: e.target.checked })} /> Følg hijri-kalenderen (Eid, Ramadan, Ashura …)</label>
            {f.hijri && <div className="row">
              <Field label="Hijri-måned">{(id) => (
                <select id={id} value={f.hijriMonth} onChange={set('hijriMonth')}>{Planner.HIJRI_MONTHS_NO.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}</select>
              )}</Field>
              <Field label="Dag" note="1–30. Første dag er den første dagen dette faller på fra og med.">{(id, nid) => (
                <input id={id} type="number" min={1} max={30} value={f.hijriDay} onChange={set('hijriDay')} aria-describedby={nid} />
              )}</Field>
            </div>}
          </>
        )}
        {f.freq && <Field label="Til og med" note={`En serie varer i maks ${Planner.MAX_YEARS} år. Tom: ett år fra første dag.`}>{(id, nid) => (
          <input id={id} type="date" value={f.until} onChange={set('until')} aria-describedby={nid} />
        )}</Field>}
      </fieldset>
      <Field label="Sted">{(id) => <input id={id} value={f.location} onChange={set('location')} maxLength={160} />}</Field>
      <Field label="Ansvarlig">{(id) => <PersonSelect id={id} value={f.ownerId} onChange={(v) => setF({ ...f, ownerId: v })} people={people} none="Ingen" />}</Field>
      {ev.allowed && <Field label="Arrangement">{(id) => <EventSelect id={id} value={f.eventId} onChange={(v) => setF({ ...f, eventId: v })} events={ev.items} />}</Field>}
      <Field label="Beskrivelse">{(id) => <textarea id={id} rows={3} value={f.description} onChange={set('description')} maxLength={2000} />}</Field>
      {!conflict && (
        <div className="row">
          <Btn small={false} disabled={!f.title.trim() || !f.startsOn || busy || saving} aria-busy={busy || saving || undefined} onClick={save}>{busy || saving ? 'Lagrer …' : 'Lagre'}</Btn>
          <Btn ghost onClick={onCancel}>Avbryt</Btn>
        </div>
      )}
      <Msg text={msg.text} tone={msg.tone} />
    </div>
  );
  // A new item is in the popup (FormDialog): its title, «×» and Escape are the popup's.
  return form;
}
